import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { ArrowRight, Mail, User, UserX, WifiOff } from "lucide-react";
import { sessionApi } from "../../api/account";
import { ApiError } from "../../api/client";
import { AuthHeading, AuthLayout, BasketNote, FormError, Notice, Repeated, SocialButtons } from "../../components/account/AuthLayout";
import { CheckEmail } from "../../components/account/CheckEmail";
import { PasswordField } from "../../components/account/PasswordField";
import { Button, Input, formatPH, isMobilePH, toNationalPH } from "../../components/ui";
import { useCart } from "../cart/useCart";
import { usePageTitle } from "../catalogue/usePageTitle";
import type { Prefill } from "./RegisterRoute";
import { errorMessage, useReturnTo, useSetSession } from "./session";
import { useSocialSession } from "./social";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** "0917 123 4567" / "+63 917…" / "maria@gmail.com" → what it is. */
function parseLogin(raw: string): { kind: "empty" | "bad" } | { kind: "email" | "phone"; value: string } {
  const typed = raw.trim();
  if (!typed) return { kind: "empty" };
  if (typed.includes("@")) return EMAIL.test(typed.toLowerCase()) ? { kind: "email", value: typed.toLowerCase() } : { kind: "bad" };
  const digits = toNationalPH(typed);
  return isMobilePH(digits) ? { kind: "phone", value: digits } : { kind: "bad" };
}

/** What was typed here, to start registering or a reset with. */
function prefillFrom(login: ReturnType<typeof parseLogin>): Prefill {
  if (login.kind === "email") return { channel: "email", email: login.value };
  if (login.kind === "phone") return { channel: "phone", phone: login.value };
  return {};
}

/**
 * PRD screens 14 and 14a: a phone number or email with a password, an emailed link for an
 * email account, or Google or Facebook (design doc §6.1). A login with no account is told
 * so and offered registering, with what was typed carried across.
 */
export function SignInRoute() {
  const navigate = useNavigate();
  const setSession = useSetSession();
  const returnTo = useReturnTo();
  const typedElsewhere = (useLocation().state as { login?: string } | null)?.login ?? "";
  const cart = useCart();
  const social = useSocialSession(returnTo);
  const [identifier, setIdentifier] = useState(typedElsewhere);
  const [password, setPassword] = useState("");
  const [problems, setProblems] = useState<{ identifier?: string; password?: string }>({});
  // Links sent to this address: after the first, the page confirms each resend.
  const [sends, setSends] = useState(0);
  const linkSent = sends > 0;
  usePageTitle(linkSent ? "Check your email" : "Sign in");
  const login = parseLogin(identifier);
  const shown = login.kind === "phone" ? `+63 ${formatPH(login.value)}` : identifier.trim();

  const signIn = useMutation({
    mutationFn: () => sessionApi.signIn(login.kind === "email" || login.kind === "phone" ? login.value : identifier, password),
    onSuccess: (session) => {
      setSession(session);
      navigate(returnTo, { replace: true });
    },
  });
  const emailLink = useMutation({
    mutationFn: () => sessionApi.emailLink(login.kind === "email" ? login.value : ""),
    onSuccess: () => setSends((count) => count + 1),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = {
      identifier: login.kind === "empty" ? "Enter your phone number or email" : login.kind === "bad" ? "Enter a mobile number like 0917 123 4567, or a full email address" : undefined,
      password: password ? undefined : "Enter your password",
    };
    setProblems(next);
    if (!next.identifier && !next.password) signIn.mutate();
  };

  if (linkSent) {
    return (
      <AuthLayout back={returnTo}>
        <CheckEmail
          email={identifier}
          purpose="sign in"
          onResend={() => emailLink.mutate()}
          resending={emailLink.isPending}
          resent={sends > 1}
          error={errorMessage(emailLink.error)}
          onChangeEmail={() => {
            emailLink.reset();
            setSends(0);
          }}
        />
      </AuthLayout>
    );
  }

  const code = signIn.error instanceof ApiError ? signIn.error.code : null;
  const wrongPassword = code === "wrong_password" ? "That password doesn't match this account" : undefined;
  const busy = signIn.isPending || emailLink.isPending || social.busy !== null;
  const edit = (change: () => void) => {
    change();
    signIn.reset();
  };

  return (
    <AuthLayout back={returnTo}>
      <AuthHeading title="Sign in">With your mobile number or email.</AuthHeading>
      <BasketNote count={cart.data?.item_count ?? 0} />
      <form onSubmit={submit} noValidate>
        <Input
          label="Phone number or email"
          icon={User}
          size="lg"
          autoComplete="username"
          placeholder="0917 123 4567 or you@example.com"
          value={identifier}
          error={problems.identifier}
          disabled={signIn.isPending}
          onChange={(event) => edit(() => {
            setIdentifier(event.target.value);
            setProblems({ ...problems, identifier: undefined });
          })}
          autoFocus
        />
        <PasswordField
          value={password}
          onChange={(value) => edit(() => {
            setPassword(value);
            setProblems({ ...problems, password: undefined });
          })}
          autoComplete="current-password"
          error={problems.password ?? wrongPassword}
          disabled={signIn.isPending}
          extra={
            <Link to="/forgot-password" state={{ from: returnTo, prefill: prefillFrom(login) }} className="inline-flex min-h-11 items-center border-b-0 type-small font-semibold">
              Forgot your password?
            </Link>
          }
        />

        {code === "no_account" && (
          <Notice
            icon={UserX}
            action={
              <Button type="button" variant="outline" size="sm" onClick={() => navigate("/register", { state: { from: returnTo, prefill: prefillFrom(login) } })}>
                Create an account
              </Button>
            }
          >
            There's no account for <Repeated>{shown}</Repeated>.
          </Notice>
        )}
        {code === "no_password" && <Notice icon={UserX}>This account signs in with Google or Facebook. Use its button below.</Notice>}
        {signIn.isError && !(signIn.error instanceof ApiError) && (
          <Notice tone="danger" icon={WifiOff}>
            We couldn't reach Kalikaya. Check your connection, then try again.
          </Notice>
        )}
        {code && !["no_account", "no_password", "wrong_password"].includes(code) && <FormError>{errorMessage(signIn.error)}</FormError>}

        <Button type="submit" fullWidth size="lg" iconRight={signIn.isPending ? undefined : ArrowRight} disabled={busy} className="mt-6">
          {signIn.isPending ? "Signing you in…" : signIn.isError && !(signIn.error instanceof ApiError) ? "Try again" : "Sign in"}
        </Button>
      </form>
      {/* Only an email account can get a link; a phone number has the code path in a reset. */}
      {login.kind === "email" && (
        <>
          <Button type="button" fullWidth size="lg" variant="outline" icon={Mail} disabled={busy} className="mt-4" onClick={() => emailLink.mutate()}>
            {emailLink.isPending ? "Sending the link…" : "Email me a sign-in link"}
          </Button>
          <FormError>{errorMessage(emailLink.error)}</FormError>
        </>
      )}

      <SocialButtons available={social.available} busy={social.busy} onChoose={social.choose} />
      <FormError>{social.error}</FormError>

      <p className="mt-7 mb-0 type-small text-text-muted">
        New here?{" "}
        <Link to="/register" state={{ from: returnTo, prefill: prefillFrom(login) }} className="font-semibold">
          Create an account
        </Link>
        . You can browse and search without one; you need one to order.
      </p>
    </AuthLayout>
  );
}
